import toast from 'react-hot-toast';
import { call, put, takeLatest } from 'redux-saga/effects';
import * as types from '../../../../constants/ActionNameConstants';
import { GET_USER_GLOSSARY_LIST_API_URL } from '../../../../constants/ApiUrlConstants';
import { GLOSSARY_GENERIC_ERROR } from '../../../../constants/GlossaryConstants';
import { postApiRequest } from '../../../../features/axiosClient';
import {
  glossaryListFailure,
  glossaryListSuccess,
  glossaryPreviewFailure,
  glossaryPreviewSuccess,
} from '../actions/glossaryActions';

const glossaryApi = (params) => postApiRequest(GET_USER_GLOSSARY_LIST_API_URL, params);

// The envelope is unwrapped here rather than in the component: JSON.parse can
// throw, and parsing in render would hand the side panel a new array identity
// on every audio timeupdate.
const normalizeGlossaryResponse = (response) => {
  // postApiRequest returns undefined on a network error and on the 401 /
  // "Blocked" paths, where it has already started a logout. Stay quiet there.
  if (!response?.data) return { ok: false, silent: true, message: GLOSSARY_GENERIC_ERROR };

  const { value, success, errorMessage, resultMessage } = response.data;
  if (!success) {
    return { ok: false, silent: false, message: errorMessage || resultMessage || GLOSSARY_GENERIC_ERROR };
  }

  // A success with no payload is how this backend spells "no rows".
  if (value === null || value === undefined || value === '') return { ok: true, items: [], totalCount: 0 };

  let parsed;
  try {
    parsed = JSON.parse(value);
  } catch (error) {
    return { ok: false, silent: false, message: GLOSSARY_GENERIC_ERROR };
  }

  return {
    ok: true,
    items: Array.isArray(parsed?.listData) ? parsed.listData : [],
    totalCount: Number(parsed?.NoOfRecords) || 0,
  };
};

// No finally blocks below: takeLatest cancellation runs finally (but not catch),
// so a put there would clobber the state of the request that replaced it.

function* handleGlossaryPreview(action) {
  const params = action.payload;
  try {
    const response = yield call(glossaryApi, params);
    const result = normalizeGlossaryResponse(response);
    if (!result.ok) {
      if (!result.silent) toast.error(result.message);
      yield put(glossaryPreviewFailure({ params, message: result.message }));
      return;
    }
    yield put(glossaryPreviewSuccess({ params, items: result.items, totalCount: result.totalCount }));
  } catch (error) {
    yield put(glossaryPreviewFailure({ params, message: error?.message || GLOSSARY_GENERIC_ERROR }));
  }
}

function* handleGlossaryList(action) {
  const params = action.payload;
  try {
    const response = yield call(glossaryApi, params);
    const result = normalizeGlossaryResponse(response);
    if (!result.ok) {
      if (!result.silent) toast.error(result.message);
      yield put(glossaryListFailure({ params, message: result.message }));
      return;
    }
    yield put(glossaryListSuccess({ params, items: result.items, totalCount: result.totalCount }));
  } catch (error) {
    yield put(glossaryListFailure({ params, message: error?.message || GLOSSARY_GENERIC_ERROR }));
  }
}

// Two watchers on two distinct types: takeLatest cancels per action type, so a
// shared type would make opening the modal cancel the panel's preview fetch.
export function* watchGlossaryPreview() {
  yield takeLatest(types.GET_GLOSSARY_PREVIEW_REQUEST, handleGlossaryPreview);
}

export function* watchGlossaryList() {
  yield takeLatest(types.GET_GLOSSARY_LIST_REQUEST, handleGlossaryList);
}
